import { useEffect, useRef, useState } from "react";
import { View, ScrollView } from "react-native";
import {
  ActionBanner,
  ActionRowCard,
  CardSurface,
  CardTitleText,
  CompactActionButton,
  Dialog,
  DialogActions,
  DialogBodyText,
  DialogHeader,
  DialogIcon,
  DialogTitleText,
  IconButton,
  MoonDetailRow,
  MoonFilterChip,
  MoonSettingRow,
  PrimaryButton,
  SecondaryButton,
  SectionTitle,
  SupportingSubtitleText,
  TextActionButton,
  colors,
  CardSubtitleText,
  TextField,
} from "@louise/moon-ui";
import {
  Calendar as CalendarIcon,
  CheckCircle2,
  ChevronLeft,
  CircleAlert,
  ClipboardList,
  HandHeart,
  Heart,
  Plus,
  ClipboardCheck,
  CalendarDays,
  BadgeCheck,
  NotebookPen,
} from "lucide-react-native";
import {
  useActiveCycle,
  useCompleteCycle,
  useUpdateCycle,
} from "@src/packages/cycles";
import {
  CANCELLATION_REASON_KEYS,
  type CycleType,
  type CancellationReason,
  type CycleOutcome,
} from "@src/packages/cycles/hooks/useCycles";
import { buildEndCycleOutcome } from "./endCycleOutcomeMerge";
import {
  outcomeTypeForOption,
  resolveEndCycleFamily,
  resolveEndCycleOptions,
  type EndCycleContext,
  type EndCycleOption,
  type EndCycleOptionId,
} from "./endCycleOptions";
import { useAuthorizedExperienceProjection } from "@src/packages/experience/hooks/useAuthorizedExperienceProjection";
import { useTranslations } from "@src/packages/i18n";
import { useLocale } from "@src/packages/i18n/useLocale";
import { getTodayString } from "@src/packages/utils/date";
import endingIllustration from "../../../../assets/images/end-treatment-cycle-illustration.png";
import { NegativeCloseoutSupport } from "../../care/modals/NegativeCloseoutSupport";
import {
  PregnancyNextSteps,
  pregnancyNextStepsSubtitleKey,
} from "../../care/modals/PregnancyNextSteps";

/**
 * What the patient can pick. Wider than the API's outcome types because
 * `completed` — how a preservation or donation cycle ends — is stored as a null
 * outcome type. See `outcomeTypeForOption`.
 */
type OutcomeType = EndCycleOptionId;

type Step = "pick" | "details" | "confirmation";

type Props = {
  visible: boolean;
  onClose: () => void;
  /** Optional: target a specific cycle. Defaults to the active cycle. */
  cycleId?: string;
  /** Optional: existing outcome id for upsert. */
  outcomeId?: string | null;
  /**
   * Optional: the ending already recorded on the target cycle.
   *
   * Needed because a PATCH replaces the whole outcome sub-object, so this modal
   * has to send back the fields it does not own or they are destroyed. Callers
   * that target a specific cycle should pass its outcome; otherwise the active
   * cycle's is used.
   */
  existingOutcome?: CycleOutcome | null;
  /** Optional: run before the cycle is updated (e.g., to snapshot history). */
  onBeforeSave?: () => Promise<void>;
  /**
   * Optional: called after the user taps Done on the confirmation step.
   *
   * Receives the ending that was actually saved. A caller that opened this modal
   * expecting one ending must not assume it got it — the picker is contextual to
   * the cycle's family, phase, and any result already recorded.
   */
  onSaved?: (savedOutcomeType: OutcomeType) => void;
  /** Optional: transfer/insemination date. Enables the pregnancy timeline. */
  transferDate?: string | null;
  /**
   * Whether a completed transfer or insemination makes a pregnancy outcome
   * meaningful for this current-cycle close. Omit for callers without phase
   * data; the modal falls back to the transfer date or recorded result.
   */
  hasPregnancyOpportunity?: boolean;
  /** Explicitly distinguish current closeout from historical outcome entry. */
  context?: EndCycleContext;
  /** Optional recap for preservation cycles completed from Finish Treatment. */
  preservationSummary?: PreservationCycleSummary;
  /** The target cycle type, used when closing preservation cycles elsewhere. */
  preservationCycleType?: CycleType | null;
  /**
   * Optional: navigate the reader to a suggested article. When provided, the
   * negative-outcome confirmation surfaces a small reading rail.
   */
  onOpenArticle?: (articleId: string) => void;
};

export type PreservationOutcomeCounts = {
  kind: "eggs" | "embryos" | "sperm";
  retrieved: number | null;
  mature: number | null;
  fertilized: number | null;
  frozen: number | null;
};

export type PreservationSpecimenSummary = {
  type: "oocyte" | "embryo" | "semen";
  count: number;
  facility?: string;
};

export type PreservationCycleSummary = {
  treatmentName: string;
  clinicName: string | null;
  startDate: string | null;
  cycleNumber: number;
  outcomeCounts: PreservationOutcomeCounts;
  specimens: PreservationSpecimenSummary[];
  onAddSpecimen?: () => void;
};

/**
 * One illustration for every ending.
 *
 * The app's dialogs carry a 3D illustration above the title — this modal was
 * drawing a lucide glyph in a tinted circle instead, a treatment used nowhere
 * else. A single neutral illustration also keeps tone in the copy where the rest
 * of this flow keeps it: art that celebrated would celebrate a loss too.
 */
const ENDING_ILLUSTRATION = endingIllustration;

const CANCELLATION_REASON_ORDER: CancellationReason[] = [
  "medical_advice",
  "personal_choice",
  "side_effects",
  "protocol_switch",
  "financial",
  "trigger_not_administered",
  "no_retrieval",
  "other",
];

function expectedStoredSpecimenType(
  kind: PreservationOutcomeCounts["kind"],
): PreservationSpecimenSummary["type"] {
  if (kind === "embryos") return "embryo";
  if (kind === "sperm") return "semen";
  return "oocyte";
}

function getStoredSpecimenCount(
  summary: PreservationCycleSummary,
  type?: PreservationSpecimenSummary["type"],
): number {
  return summary.specimens.reduce((sum, specimen) => {
    return sum + (type == null || specimen.type === type ? specimen.count : 0);
  }, 0);
}

function hasStoredSpecimenMismatch(summary: PreservationCycleSummary): boolean {
  const expectedCount = summary.outcomeCounts.frozen;
  if (summary.outcomeCounts.kind === "sperm") {
    // Sperm closeout must reconcile the reported vial count with current-cycle
    // inventory. Null means the outcome has not been recorded.
    return (
      expectedCount == null ||
      getStoredSpecimenCount(
        summary,
        expectedStoredSpecimenType(summary.outcomeCounts.kind),
      ) !== expectedCount
    );
  }
  if (expectedCount == null || expectedCount <= 0) return false;
  return (
    getStoredSpecimenCount(
      summary,
      expectedStoredSpecimenType(summary.outcomeCounts.kind),
    ) !== expectedCount
  );
}

export default function EndCycleModal({
  visible,
  onClose,
  cycleId,
  outcomeId,
  existingOutcome,
  onBeforeSave,
  onSaved,
  transferDate,
  hasPregnancyOpportunity: hasPregnancyOpportunityProp,
  context = "current_cycle_close",
  preservationSummary,
  preservationCycleType,
  onOpenArticle,
}: Props) {
  const t = useTranslations();
  const { data: activeCycle } = useActiveCycle();
  const updateCycle = useUpdateCycle();
  const completeCycle = useCompleteCycle();
  const [step, setStep] = useState<Step>("pick");
  const [outcomeType, setOutcomeType] = useState<OutcomeType | null>(null);
  const [cancellationReason, setCancellationReason] =
    useState<CancellationReason | null>(null);
  const [notes, setNotes] = useState("");

  /**
   * Which endings this cycle can have. A preservation or donation cycle cannot
   * end in a pregnancy, and until now had no way to say it simply finished — so
   * completing egg freezing had to be filed as a result.
   */
  const authorizedExperience = useAuthorizedExperienceProjection();
  const targetCycleId = cycleId ?? activeCycle?.id;
  const targetOutcomeId =
    outcomeId !== undefined ? outcomeId : (activeCycle?.outcome?.id ?? null);
  const isPreservation =
    preservationSummary != null ||
    preservationCycleType === "egg_freezing" ||
    preservationCycleType === "embryo_freezing" ||
    preservationCycleType === "sperm_freezing";
  const treatmentGoal =
    authorizedExperience.status === "ready"
      ? (authorizedExperience.projection?.context.familyBuildingPlan
          .treatmentGoal ?? "unknown")
      : "unknown";
  const endCycleFamily = resolveEndCycleFamily({
    cycleType: preservationCycleType ?? activeCycle?.cycleType,
    treatmentGoal,
  });

  /**
   * Preselect the ending that is already recorded, if any.
   *
   * A cycle can reach this modal with its outcome already saved — recording a
   * beta result no longer closes the cycle, so the patient may be ending a cycle
   * whose result is known. Starting on an empty selection would ask them to
   * state it a second time, and saving replaces the whole outcome sub-object, so
   * a different pick would silently overwrite what was recorded.
   */
  // Prefer the caller's outcome: when a `cycleId` is passed the target may not be
  // the active cycle, and preserving another cycle's fields onto it would be
  // worse than preserving nothing.
  const recordedOutcome =
    existingOutcome !== undefined
      ? existingOutcome
      : (activeCycle?.outcome ?? null);
  const recordedOutcomeType = recordedOutcome?.type ?? null;
  const hasRecordedOutcome = recordedOutcome != null;
  const recordedCancellationReason =
    recordedOutcome?.cancellationReason ?? null;
  const hasPregnancyOpportunity =
    hasPregnancyOpportunityProp ??
    (transferDate != null ||
      recordedOutcomeType === "pregnancy" ||
      recordedOutcomeType === "no_pregnancy" ||
      recordedOutcomeType === "miscarriage");
  const { options: resolvedOutcomeOptions, promptKey } = resolveEndCycleOptions(
    {
      family: endCycleFamily,
      context,
      hasPregnancyOpportunity,
      recordedOutcomeType,
      hasRecordedOutcome,
    },
  );
  const outcomeOptions = resolvedOutcomeOptions;
  const showRecordedResultRecap =
    context === "current_cycle_close" &&
    !preservationSummary &&
    hasRecordedOutcome &&
    recordedOutcomeType != null;
  useEffect(() => {
    if (!visible) return;
    if (outcomeType) return;
    if (!recordedOutcomeType) return;
    setOutcomeType(recordedOutcomeType);
    if (
      recordedOutcomeType === "cancelled" &&
      recordedCancellationReason &&
      recordedCancellationReason in CANCELLATION_REASON_KEYS
    ) {
      setCancellationReason(recordedCancellationReason as CancellationReason);
    }
  }, [visible, outcomeType, recordedOutcomeType, recordedCancellationReason]);

  /**
   * True from a successful write until the confirmation it leads to is dismissed.
   *
   * The window the reset below has to keep its hands off; see the comment there.
   */
  const justSaved = useRef(false);

  /**
   * The transfer date as it stood when the save went out.
   *
   * The confirmation step draws its pregnancy timeline — first scan, end of the
   * first trimester, halfway — from `transferDate`, and every caller derives that
   * from the cycle's phases. Closing invalidates the cycle, so the phases re-key to
   * nothing and the prop goes null a moment after the step appears: the timeline
   * showed up and then vanished while she was reading it. Held here, the step keeps
   * the dates the cycle had when she closed it.
   */
  const [savedTransferDate, setSavedTransferDate] = useState<string | null>(
    null,
  );

  const reset = () => {
    setStep("pick");
    setOutcomeType(null);
    setCancellationReason(null);
    setNotes("");
    setSavedTransferDate(null);
    justSaved.current = false;
  };

  // Reset when the target cycle changes. Native Stack reuses this component
  // when the CycleDetail route swaps cycleId (e.g. after creating a new
  // cycle from CryoVault), so without this reset a leftover
  // `step === "confirmation"` from the previous cycle would flash on the
  // new one.
  //
  // What it must not do is undo the save's own last step. Closing invalidates
  // `active-cycle`, callers pass that cycle's id, so the id this modal was given
  // drops the moment the write lands — and resetting there sent the modal back to
  // "pick" with the outcome cleared. The recap could not render either, the closed
  // cycle's record no longer being readable, so the patient who had just confirmed
  // her result was handed a fresh "how do you want to close this cycle?" for a
  // cycle that was already closed, on a step that could do nothing: with no cycle
  // `targetCycleId` is undefined and `handleSave` returns immediately.
  //
  // The signal is the save, not the shape of the new id. Keying on "the id
  // disappeared" also skipped the reset when a specific cycle gave way to the
  // default active one with none open — a genuine target change, and one this
  // effect was written for — leaving another cycle's step and notes on screen with
  // no way to clear them. `justSaved` is only ever true between a successful write
  // and the confirmation being dismissed, which is exactly the window to protect.
  useEffect(() => {
    if (justSaved.current) return;
    setStep("pick");
    setOutcomeType(null);
    setCancellationReason(null);
    setNotes("");
    // Cleared here too, or a genuine target change reset everything except the
    // previous cycle's transfer date and left it to be read on the next one.
    setSavedTransferDate(null);
  }, [cycleId]);

  /**
   * Hiding the modal ends the window `justSaved` protects, however it was hidden.
   *
   * `reset()` was the only thing clearing it, and `reset()` only runs from paths
   * that go through this modal — so a parent flipping `visible` off on its own
   * (`CycleDetailScreen`'s "add specimen" does exactly that) left the flag true
   * for the rest of the mount, and the reset above never fired again. That is the
   * leftover-confirmation bug its own comment describes, reinstated. Keyed on
   * `visible`, the invariant holds without anyone having to remember it.
   */
  useEffect(() => {
    if (visible) return;
    justSaved.current = false;
  }, [visible]);

  const handleClose = () => {
    onClose();
    reset();
  };

  const handleDone = () => {
    // Captured before `reset()` clears it, and only reported when something was
    // actually saved — the confirmation step is unreachable otherwise.
    const savedOutcomeType = outcomeType;
    onClose();
    reset();
    if (savedOutcomeType) onSaved?.(savedOutcomeType);
  };

  const handleSelectOutcome = (nextOutcomeType: OutcomeType) => {
    setOutcomeType(nextOutcomeType);
    if (nextOutcomeType !== "cancelled") setCancellationReason(null);
    setStep("details");
  };

  const handleBack = () => {
    setStep("pick");
  };

  const handleSave = async () => {
    if (!targetCycleId || (!preservationSummary && !outcomeType)) return;
    const today = getTodayString();

    try {
      await onBeforeSave?.();
      if (preservationSummary) {
        await completeCycle.mutateAsync({
          cycleId: targetCycleId,
          notes: notes.trim() || undefined,
          outcomeId: targetOutcomeId ?? undefined,
        });
        onClose();
        reset();
        onSaved?.("completed");
        return;
      }
      if (!outcomeType) return;
      await updateCycle.mutateAsync({
        cycleId: targetCycleId,
        data: {
          // Preserves the fields this modal does not own; see
          // `buildEndCycleOutcome`.
          outcome: {
            ...(targetOutcomeId ? { id: targetOutcomeId } : {}),
            ...buildEndCycleOutcome({
              // `completed` stores a null type; see `outcomeTypeForOption`.
              outcomeType: outcomeTypeForOption(outcomeType),
              cancellationReason,
              addedNote: notes,
              today,
              recorded: recordedOutcome,
            }),
          },
          status: "closed",
          endDate: today,
        },
      });
      // Both before the step change, so the confirmation renders once with the
      // cycle's data already held rather than reading a cycle that is on its way
      // out. See `justSaved` and `savedTransferDate`.
      justSaved.current = true;
      setSavedTransferDate(transferDate ?? null);
      setStep("confirmation");
    } catch {
      // Keep the details step open; the error banner renders from mutation state.
    }
  };

  const isLoading = updateCycle.isPending || completeCycle.isPending;
  // The raw error is for the logs, not for her: a failed save gets one
  // translated line, whatever the cause.
  const saveFailed = completeCycle.isError || updateCycle.isError;

  return (
    <Dialog
      size="wide"
      visible={visible}
      onClose={step === "confirmation" ? handleDone : handleClose}
      layout="centered"
      closeButton="minimal"
      testID="end-cycle-modal"
    >
      {step === "details" ? (
        <IconButton
          accessibilityLabel={t("homepage.review_concept.post_procedure.back")}
          action="homepage.review_concept.post_procedure.back"
          onPress={handleBack}
          hitSlop={8}
          size="sm"
          pressedOpacity={1}
          style={{
            position: "absolute",
            top: 12,
            left: 12,
            zIndex: 2,
          }}
        >
          <ChevronLeft size={20} color={colors.textLight} strokeWidth={2} />
        </IconButton>
      ) : null}
      {step === "pick" && showRecordedResultRecap ? (
        <RecordedResultRecapStep
          recordedOutcome={recordedOutcome}
          recordedOutcomeType={recordedOutcomeType}
          onConfirm={handleSave}
          onChooseOther={() => handleSelectOutcome("other")}
          saveFailed={saveFailed}
          isLoading={isLoading}
          canConfirm={!!targetCycleId}
          t={t}
        />
      ) : step === "pick" ? (
        <PickStep
          options={outcomeOptions}
          promptKey={promptKey}
          onSelectOutcome={handleSelectOutcome}
          notes={notes}
          setNotes={setNotes}
          saveFailed={saveFailed}
          isLoading={isLoading}
          /**
           * A specimen count that disagrees with the recorded result is worth
           * saying, and it is not a reason to make a cycle permanently
           * unclosable.
           *
           * It used to gate this button, and the dead end was real: the
           * mismatch can come from a specimen attached to a different cycle —
           * something she cannot repair from this dialog, since the only
           * affordance here adds *more* specimens and pushes the count further
           * out. A cycle in that state could never be ended at all.
           *
           * The warning above stays, and stays prominent. Ending the cycle is
           * her decision, available at any point; the record keeps both numbers
           * so the disagreement remains visible to her care team rather than
           * being resolved by a button she was not allowed to press.
           */
          canConfirm={
            !!targetCycleId && (!!preservationSummary || !!outcomeType)
          }
          onCancel={handleClose}
          onConfirm={handleSave}
          preservationSummary={preservationSummary}
          isPreservation={isPreservation}
          t={t}
        />
      ) : step === "details" ? (
        <DetailsStep
          outcomeType={outcomeType}
          cancellationReason={cancellationReason}
          setCancellationReason={setCancellationReason}
          notes={notes}
          setNotes={setNotes}
          saveFailed={saveFailed}
          isLoading={isLoading}
          onConfirm={handleSave}
          t={t}
        />
      ) : (
        <ConfirmationStep
          type={outcomeType}
          // Held at save time: the live prop is derived from the cycle this write
          // just closed, so it empties out from under the step.
          // Only the value held at save time. Falling back to the live prop put
          // the coupling back: both callers derive it from phases that empty out
          // during the close, and on Plan home the closed cycle leaves the
          // transition slot — which can re-resolve to *another* open cycle, so
          // this cycle's confirmation would date its scan and trimester from that
          // one's transfer.
          transferDate={savedTransferDate}
          onDone={handleDone}
          onOpenArticle={onOpenArticle}
          t={t}
        />
      )}
    </Dialog>
  );
}

// ── Pick step ───────────────────────────────────────────────

function NotesField({
  value,
  onChangeText,
  placeholder,
}: {
  value: string;
  onChangeText: (value: string) => void;
  placeholder: string;
}) {
  return (
    <TextField
      notes
      placeholder={placeholder}
      accessibilityLabel={placeholder}
      value={value}
      onChangeText={onChangeText}
      style={{ marginTop: 14, marginBottom: 20 }}
    />
  );
}

function SaveErrorBanner({ t }: { t: (key: string) => string }) {
  return (
    <View style={{ marginBottom: 14 }}>
      <ActionBanner
        variant="important"
        message={t("you.modals.end_cycle.could_not_save_cycle")}
      />
    </View>
  );
}

function outcomeIcon(optionId: EndCycleOptionId) {
  switch (optionId) {
    case "pregnancy":
      return <Heart size={17} color={colors.textMid} />;
    case "no_pregnancy":
      return <CircleAlert size={17} color={colors.textMid} />;
    case "miscarriage":
      return <HandHeart size={17} color={colors.textMid} />;
    case "cancelled":
      return <CalendarIcon size={17} color={colors.textMid} />;
    case "completed":
      return <CheckCircle2 size={17} color={colors.textMid} />;
    default:
      return <ClipboardList size={17} color={colors.textMid} />;
  }
}

function recordedOutcomeLabelKey(
  outcomeType: Exclude<EndCycleOptionId, "completed"> | null,
): string {
  switch (outcomeType) {
    case "pregnancy":
      return "you.modals.end_cycle.outcome_pregnancy";
    case "no_pregnancy":
      return "you.modals.end_cycle.outcome_no_pregnancy";
    case "miscarriage":
      return "you.modals.end_cycle.outcome_miscarriage";
    case "cancelled":
      return "you.modals.end_cycle.outcome_cancelled";
    default:
      return "you.modals.end_cycle.outcome_other";
  }
}

function recordedResultStatusKey(
  status: CycleOutcome["resultStatus"] | undefined,
): string | null {
  switch (status) {
    case "positive":
      return "you.modals.end_cycle.recorded_result_positive_status";
    case "negative":
      return "you.modals.end_cycle.recorded_result_negative_status";
    case "uncertain":
      return "you.modals.end_cycle.recorded_result_uncertain_status";
    default:
      return null;
  }
}

function RecordedResultRecapStep({
  recordedOutcome,
  recordedOutcomeType,
  onConfirm,
  onChooseOther,
  saveFailed,
  isLoading,
  canConfirm,
  t,
}: {
  recordedOutcome: CycleOutcome | null;
  recordedOutcomeType: Exclude<EndCycleOptionId, "completed"> | null;
  onConfirm: () => void;
  onChooseOther: () => void;
  saveFailed: boolean;
  isLoading: boolean;
  canConfirm: boolean;
  t: (key: string) => string;
}) {
  const { locale } = useLocale();
  const statusKey = recordedResultStatusKey(recordedOutcome?.resultStatus);
  const notes = recordedOutcome?.notes?.trim() ?? "";

  return (
    <>
      <View style={{ alignItems: "center", gap: 8, marginBottom: 18 }}>
        <DialogIcon
          source={ENDING_ILLUSTRATION}
          width={68}
          height={72}
          style={{ marginBottom: 2 }}
          testID="end-cycle-recorded-recap-illustration"
        />
        <DialogHeader
          align="center"
          title={t("you.modals.end_cycle.recorded_result_recap_title")}
          subtitle={t("you.modals.end_cycle.recorded_result_recap_prompt")}
          style={{ width: "100%" }}
          subtitleStyle={{ textAlign: "center", width: "100%" }}
        />
      </View>

      <CardSurface style={{ padding: 16, gap: 14, marginBottom: 16 }}>
        <MoonDetailRow
          icon={ClipboardCheck}
          label={t("you.modals.end_cycle.recorded_result_label")}
          value={
            <CardTitleText>
              {t(recordedOutcomeLabelKey(recordedOutcomeType))}
            </CardTitleText>
          }
        />
        {recordedOutcome?.dateRecorded ? (
          <MoonDetailRow
            icon={CalendarDays}
            label={t("you.modals.end_cycle.recorded_result_date_label")}
            value={formatLong(recordedOutcome.dateRecorded, locale)}
          />
        ) : null}
        {statusKey ? (
          <MoonDetailRow
            icon={BadgeCheck}
            label={t("you.modals.end_cycle.recorded_result_status_label")}
            value={t(statusKey)}
          />
        ) : null}
        {notes ? (
          <MoonDetailRow
            icon={NotebookPen}
            label={t("you.modals.end_cycle.recorded_result_notes_label")}
            value={
              <CardSubtitleText style={{ marginTop: 0 }}>
                {notes}
              </CardSubtitleText>
            }
          />
        ) : null}
      </CardSurface>

      {saveFailed ? <SaveErrorBanner t={t} /> : null}

      <DialogActions>
        <PrimaryButton
          label={t("you.modals.end_cycle.close_with_recorded_result")}
          action="you.modals.end_cycle.close_with_recorded_result"
          onPress={onConfirm}
          loading={isLoading}
          disabled={!canConfirm || isLoading}
        />
      </DialogActions>
      <TextActionButton
        label={t("you.modals.end_cycle.choose_different_outcome")}
        action="end_cycle_choose_other_outcome"
        tone="secondary"
        centered
        onPress={onChooseOther}
        style={{ marginTop: 8 }}
      />
    </>
  );
}

function PickStep({
  options,
  promptKey,
  onSelectOutcome,
  notes,
  setNotes,
  saveFailed,
  isLoading,
  canConfirm,
  onCancel,
  onConfirm,
  preservationSummary,
  isPreservation,
  t,
}: {
  options: EndCycleOption[];
  promptKey: string;
  onSelectOutcome: (v: OutcomeType) => void;
  notes: string;
  setNotes: (s: string) => void;
  saveFailed: boolean;
  isLoading: boolean;
  canConfirm: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  preservationSummary?: PreservationCycleSummary;
  isPreservation: boolean;
  t: (key: string) => string;
}) {
  const titleKey = isPreservation
    ? "care.complete_cycle.complete_this_cycle"
    : "you.modals.end_cycle.end_treatment_cycle";
  const subtitleKey = isPreservation
    ? "care.complete_cycle.marks_cycle_complete_keeps_history"
    : promptKey;

  return (
    <>
      <View style={{ alignItems: "center", gap: 8, marginBottom: 18 }}>
        <DialogIcon
          source={ENDING_ILLUSTRATION}
          width={68}
          height={72}
          style={{ marginBottom: 2 }}
          testID="end-cycle-picker-illustration"
        />
        <DialogHeader
          align="center"
          title={t(titleKey)}
          subtitle={t(subtitleKey)}
          style={{ width: "100%" }}
          subtitleStyle={{ textAlign: "center", width: "100%" }}
        />
      </View>
      {isPreservation && preservationSummary ? (
        <PreservationRecap summary={preservationSummary} />
      ) : (
        <ScrollView
          style={{ maxHeight: 430 }}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ gap: 10 }}
        >
          {options.map((option) => (
            <ActionRowCard
              key={option.id}
              leading={outcomeIcon(option.id)}
              title={t(option.labelKey)}
              subtitle={t(option.descriptionKey)}
              onPress={() => onSelectOutcome(option.id)}
              action="end_cycle_outcome_option"
              accessibilityLabel={`${t(option.labelKey)}. ${t(option.descriptionKey)}`}
            />
          ))}
        </ScrollView>
      )}

      {preservationSummary ? (
        <NotesField
          placeholder={t("you.modals.end_cycle.notes_placeholder")}
          value={notes}
          onChangeText={setNotes}
        />
      ) : null}

      {saveFailed ? <SaveErrorBanner t={t} /> : null}

      {preservationSummary ? (
        <DialogActions>
          <SecondaryButton
            label={t("you.modals.end_cycle.cancel")}
            action="you.modals.end_cycle.cancel"
            onPress={onCancel}
            flex={1}
          />
          <PrimaryButton
            label={t("care.complete_cycle.complete_cycle")}
            action="care.complete_cycle.complete_cycle"
            onPress={onConfirm}
            loading={isLoading}
            disabled={!canConfirm}
            flex={1}
          />
        </DialogActions>
      ) : null}
    </>
  );
}

function DetailsStep({
  outcomeType,
  cancellationReason,
  setCancellationReason,
  notes,
  setNotes,
  saveFailed,
  isLoading,
  onConfirm,
  t,
}: {
  outcomeType: OutcomeType | null;
  cancellationReason: CancellationReason | null;
  setCancellationReason: (value: CancellationReason | null) => void;
  notes: string;
  setNotes: (value: string) => void;
  saveFailed: boolean;
  isLoading: boolean;
  onConfirm: () => void;
  t: (key: string) => string;
}) {
  const isCancelled = outcomeType === "cancelled";
  const isOther = outcomeType === "other";
  const titleKey = isCancelled
    ? "you.modals.end_cycle.cancelled_details_title"
    : isOther
      ? "you.modals.end_cycle.tell_us_what_happened"
      : "you.modals.end_cycle.outcome_details_title";
  const promptKey = isCancelled
    ? "you.modals.end_cycle.cancelled_details_prompt"
    : isOther
      ? "you.modals.end_cycle.other_details_prompt"
      : "you.modals.end_cycle.outcome_details_prompt";

  return (
    <>
      <View style={{ alignItems: "center", gap: 8, marginBottom: 18 }}>
        <DialogIcon
          source={ENDING_ILLUSTRATION}
          width={68}
          height={72}
          style={{ marginBottom: 2 }}
          testID="end-cycle-details-illustration"
        />
        <DialogHeader
          align="center"
          title={t(titleKey)}
          subtitle={t(promptKey)}
          style={{ width: "100%" }}
          subtitleStyle={{ textAlign: "center", width: "100%" }}
        />
      </View>

      {isCancelled ? (
        <View
          style={{
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 8,
            marginBottom: 14,
          }}
        >
          {CANCELLATION_REASON_ORDER.map((value) => {
            const isSelected = cancellationReason === value;
            return (
              <MoonFilterChip
                key={value}
                label={t(CANCELLATION_REASON_KEYS[value])}
                action="cancellation_reason_option"
                tone="glass"
                selected={isSelected}
                onPress={() => setCancellationReason(isSelected ? null : value)}
              />
            );
          })}
        </View>
      ) : null}

      <NotesField
        placeholder={t("you.modals.end_cycle.notes_placeholder")}
        value={notes}
        onChangeText={setNotes}
      />

      {saveFailed ? <SaveErrorBanner t={t} /> : null}

      <DialogActions>
        <PrimaryButton
          label={t("you.modals.end_cycle.end_cycle")}
          action="you.modals.end_cycle.end_cycle"
          onPress={onConfirm}
          loading={isLoading}
          disabled={isLoading}
        />
      </DialogActions>
    </>
  );
}

type OutcomeField = [Exclude<keyof PreservationOutcomeCounts, "kind">, string];

const FERTILIZED_FIELD: OutcomeField[] = [
  ["fertilized", "care.complete_cycle.fertilized_count"],
];

function PreservationRecap({ summary }: { summary: PreservationCycleSummary }) {
  const t = useTranslations();
  const { locale } = useLocale();
  const { kind } = summary.outcomeCounts;
  const totalSpecimens = getStoredSpecimenCount(summary);
  const matchingStoredSpecimens = getStoredSpecimenCount(
    summary,
    expectedStoredSpecimenType(kind),
  );
  const storedSpecimensMismatch = hasStoredSpecimenMismatch(summary);
  const hasFrozenCount =
    summary.outcomeCounts.frozen != null && summary.outcomeCounts.frozen > 0;
  const storedSpecimenCount = hasFrozenCount
    ? matchingStoredSpecimens
    : totalSpecimens;
  const confirmedNoStoredSpecimens =
    summary.outcomeCounts.frozen === 0 && totalSpecimens === 0;
  const cycleDetails = [
    summary.clinicName,
    summary.startDate
      ? t("care.complete_cycle.started_on_date", {
          params: { date: formatLong(summary.startDate, locale) },
        })
      : null,
  ]
    .filter(Boolean)
    .join(" · ");
  // A sperm cycle has no retrieval or maturity step, so its outcome is the
  // vial count alone.
  const outcomeFields: OutcomeField[] =
    kind === "sperm"
      ? [["frozen", "care.complete_cycle.vials_frozen_count"]]
      : [
          ["retrieved", "care.complete_cycle.retrieved_count"],
          ["mature", "care.complete_cycle.mature_count"],
          ...(kind === "embryos" ? FERTILIZED_FIELD : []),
          [
            "frozen",
            kind === "embryos"
              ? "care.complete_cycle.embryos_frozen_count"
              : "care.complete_cycle.frozen_count",
          ],
        ];
  const notice = storedSpecimensMismatch
    ? // A sperm cycle can reach here with no count recorded at all, and
      // `String(null)` put the word "null" in front of the patient. Nothing to
      // compare, so say that instead.
      summary.outcomeCounts.frozen == null
      ? t("care.complete_cycle.no_frozen_count_recorded")
      : t("care.complete_cycle.stored_specimens_count_mismatch", {
          params: {
            expectedCount: String(summary.outcomeCounts.frozen),
            storedCount: String(matchingStoredSpecimens),
          },
        })
    : confirmedNoStoredSpecimens
      ? t("care.complete_cycle.confirmed_no_stored_specimens")
      : null;

  return (
    <ScrollView
      style={{ maxHeight: 330, marginBottom: 16 }}
      contentContainerStyle={{ paddingBottom: 8 }}
      showsVerticalScrollIndicator={false}
    >
      <CardSurface style={{ paddingVertical: 4 }}>
        <View
          style={{ paddingHorizontal: 16, paddingTop: 12, paddingBottom: 6 }}
        >
          <CardTitleText>{`${summary.treatmentName} #${summary.cycleNumber}`}</CardTitleText>
          {cycleDetails ? (
            <SupportingSubtitleText>{cycleDetails}</SupportingSubtitleText>
          ) : null}
        </View>

        <SectionTitle
          level="subsection"
          style={{ paddingHorizontal: 16, paddingTop: 10 }}
        >
          {t("care.complete_cycle.outcome_review_header")}
        </SectionTitle>
        {outcomeFields.map(([field, labelKey]) => {
          const count = summary.outcomeCounts[field];
          return (
            <MoonSettingRow
              key={field}
              label={t(labelKey)}
              value={
                count == null
                  ? t("care.complete_cycle.not_recorded")
                  : String(count)
              }
              valueColor={count == null ? colors.textFaint : colors.text}
            />
          );
        })}
        {storedSpecimenCount > 0 ? (
          <MoonSettingRow
            label={t("care.complete_cycle.stored_specimens_label")}
            value={String(storedSpecimenCount)}
            valueColor={colors.text}
          />
        ) : null}

        {notice ? (
          <View style={{ paddingHorizontal: 16, paddingBottom: 14, gap: 12 }}>
            <SupportingSubtitleText>{notice}</SupportingSubtitleText>
            {storedSpecimensMismatch && summary.onAddSpecimen ? (
              <CompactActionButton
                label={t("care.complete_cycle.add_stored_specimens")}
                action="care.complete_cycle.add_stored_specimens"
                leadingIcon={Plus}
                onPress={summary.onAddSpecimen}
                style={{ alignSelf: "flex-start" }}
              />
            ) : null}
          </View>
        ) : null}
      </CardSurface>
    </ScrollView>
  );
}

// ── Confirmation step ───────────────────────────────────────

function ConfirmationStep({
  type,
  transferDate,
  onDone,
  onOpenArticle,
  t,
}: {
  type: OutcomeType | null;
  transferDate: string | null;
  onDone: () => void;
  onOpenArticle?: (articleId: string) => void;
  t: (key: string) => string;
}) {
  const isPositive = type === "pregnancy";
  // A preservation or donation cycle that finished is neither a result nor a
  // loss. Sending it down the negative branch would tell someone who completed
  // egg freezing "we're so sorry".
  const isCompletion = type === "completed";

  return (
    <>
      {/* Illustration above a centred title, the same shape every other dialog
          in the app uses. */}
      <View style={{ alignItems: "center", gap: 8, marginBottom: 16 }}>
        <DialogIcon
          source={ENDING_ILLUSTRATION}
          width={72}
          height={76}
          testID="end-cycle-confirmation-illustration"
        />
        <DialogTitleText style={{ textAlign: "center" }}>
          {isCompletion
            ? t("you.modals.end_cycle.confirm_completed_title")
            : isPositive
              ? t("care.beta_result.such_wonderful_news")
              : t("care.beta_result.header_with_you")}
        </DialogTitleText>
        {isPositive ? (
          <DialogBodyText style={{ textAlign: "center", marginTop: 0 }}>
            {t(pregnancyNextStepsSubtitleKey(transferDate))}
          </DialogBodyText>
        ) : null}
      </View>

      <ScrollView
        showsVerticalScrollIndicator={false}
        style={{ maxHeight: 460 }}
        contentContainerStyle={{ gap: 18, paddingBottom: 4 }}
      >
        {isCompletion ? (
          // No pregnancy timeline and no bereavement resources: neither belongs
          // on a cycle that finished as intended.
          <DialogBodyText style={{ textAlign: "center", marginTop: 0 }}>
            {t("you.modals.end_cycle.confirm_completed_body")}
          </DialogBodyText>
        ) : isPositive ? (
          <PregnancyNextSteps transferDate={transferDate} />
        ) : (
          <NegativeCloseoutSupport onOpenArticle={onOpenArticle} />
        )}
        <PrimaryButton
          label={t("care.beta_result.done")}
          action="end_cycle_done"
          onPress={onDone}
          fullWidth
        />
      </ScrollView>
    </>
  );
}

function formatLong(iso: string | null, locale: string): string {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(d);
}

// ── Negative body ──────────────────────────────────────────
